export type Language = "en" | "pt-PT" | "pt-BR";

/**
 * Map display language names (as stored in localStorage / DB) to Language codes.
 * Falls back to "en" for unknown values.
 */
export function toLanguageCode(displayName: string): Language {
  const lower = displayName.toLowerCase();
  if (lower === "pt-pt" || lower === "portuguese" || lower === "português (portugal)") return "pt-PT";
  if (lower === "pt-br" || lower === "português (brasil)") return "pt-BR";
  return "en";
}

const translations: Record<Language, Record<string, string>> = {
  en: {
    // Chat placeholder
    "chat.placeholder.title": "Tell me about your idea",
    "chat.placeholder.subtitle":
      "Describe your business idea or product concept. I'll ask questions to understand it fully, then generate a viability assessment.",
    "chat.examples.heading": "Founders have tested ideas like:",
    "chat.examples.1": "AI marketplace connecting freelance accountants with small businesses",
    "chat.examples.2": "A tool that helps parents track their kids' medication schedules",
    "chat.examples.3": "Browser extension that summarizes terms of service before you accept",
    "chat.examples.4": "We're building a deployment rollback tool for small engineering teams. When a deploy causes errors, instead of just reverting to the last version, our tool analyzes logs, identifies the specific breaking change, and suggests a minimal patch. We'd target teams of 5-20 devs who don't have dedicated SRE staff. Free tier monitors one service, paid plan at $79/mo covers unlimited services. We have a working prototype integrated with GitHub Actions but no users yet.",
    "chat.examples.5": "I'm building a platform that connects homeowners with certified energy auditors for pre-sale home assessments. The idea is that sellers get an energy efficiency report they can use to justify higher asking prices, and auditors get a lead pipeline instead of cold-calling. We'd take 15% of the audit fee. Starting in Portugal and Spain where new EU energy regulations are making these audits mandatory. I've talked to three auditors who said they'd join but haven't built anything yet.",
    "chat.input.placeholder.first": "Describe your business idea...",
    "chat.input.placeholder.reply": "Type your response...",
    "chat.input.placeholder.feedback": "Share your thoughts...",
    "chat.input.placeholder.done": "Feedback complete",
    "chat.send": "Send",
    "chat.thinking": "Thinking...",
    "chat.restoring": "Restoring conversation...",
    "chat.export.title.feedback": "Feedback Interview",
    "chat.export.title.main": "Business Interrogation",

    // Phase 0 progress
    "phase0.step1": "Researching your market...",
    "phase0.step2": "Analyzing competitors and funding rounds...",
    "phase0.step3": "Gathering market data and pricing benchmarks...",
    "phase0.step4": "Synthesizing research into your first question...",
    "phase0.step5":
      "Almost done -- preparing a detailed opening based on market research...",
    "phase0.note":
      "This takes longer than regular responses -- we're doing deep market research to give you specific competitor data and market context in our first question.",

    // Conversation progress (intermediate rounds)
    "conversation.step1": "Analyzing your response...",
    "conversation.step2": "Preparing the next question...",
    "conversation.step3": "Evaluating your answers so far...",
    "conversation.note":
      "Each question builds on your previous answers to probe deeper into your business idea.",

    // Assessment progress (reflects 3-call enhanced synthesis)
    "assessment.step1": "Generating your viability assessment...",
    "assessment.step2": "Building executive summary and investigation analysis...",
    "assessment.step3": "Analyzing dimensions 1 through 5...",
    "assessment.step4": "Analyzing dimensions 6 through 10...",
    "assessment.step5": "Compiling competitive intelligence and risk action plans...",
    "assessment.step6": "Writing your detailed assessment report...",
    "assessment.step7":
      "Finalizing -- this is a comprehensive report, almost ready...",
    "assessment.note":
      "This takes longer than regular responses -- we're preparing your full 10-dimension viability report with competitive intelligence, risk action plans, and recommendations.",

    // Assessment / brief
    "assessment.download": "Download Assessment",
    "assessment.download.partial": "Download Partial Assessment",
    "assessment.download.html": "View Full Assessment",
    "assessment.download.exec": "View Executive Summary",
    "assessment.download.md": "Download Markdown",
    "assessment.incomplete.warning":
      "This assessment may be incomplete due to a technical issue. The key findings are included above.",
    "assessment.complete.pilot": "Assessment complete!",
    "assessment.complete.default": "Business brief complete!",
    "assessment.ready.pilot":
      "Your viability assessment is ready. Download it below.",
    "assessment.ready.default":
      "Your brief is ready. Generate a business case to review before building the full plan.",
    "assessment.generate_bc": "Generate Business Case",

    // Feedback
    "feedback.banner": "Feedback Interview",
    "feedback.prompt.title": "How was your experience?",
    "feedback.prompt.body":
      "Help us improve by sharing a quick feedback interview (2-3 minutes).",
    "feedback.cta": "Give Feedback",
    "feedback.later": "Maybe later",
    "feedback.complete": "Thanks for your feedback!",
    "feedback.back": "Back to Dashboard",

    // Quick feedback
    "feedback.quick.title": "Quick feedback",
    "feedback.quick.placeholder": "What's on your mind?",
    "feedback.quick.send": "Send",
    "feedback.quick.sending": "Sending...",
    "feedback.quick.thanks": "Thanks for your feedback!",
    "feedback.quick.button": "Feedback",

    // Return nudge
    "nudge.message":
      "Your assessment is ready! We'd love to hear your thoughts.",
    "nudge.cta": "Give Feedback",

    // Upsell
    "upsell.title": "What Architect can do next with your business:",
    "upsell.assessment.name": "Full Assessment",
    "upsell.assessment.price": "$49",
    "upsell.assessment.tagline": "Unlock your complete assessment",
    "upsell.assessment.desc":
      "Full 10-dimension assessment with structured mathematical models, competitive intelligence dossier, risk register with directional recommendations, founder decision pattern analysis, and shareable executive summary PDF.",
    "upsell.assessment.cta": "Unlock Full Assessment -- $49",
    "upsell.bizplan.name": "Business Plan",
    "upsell.bizplan.price": "$249",
    "upsell.bizplan.tagline": "Get a complete business strategy",
    "upsell.bizplan.desc":
      "Full business case with market sizing, competitive positioning, revenue model, go-to-market playbook, product specification, risk resolution roadmap, and 90-day execution plan. Built on your assessment findings.",
    "upsell.bizplan.includes": "Everything in Assessment, plus:",
    "upsell.bizplan.bullet1": "Market sizing & positioning strategy",
    "upsell.bizplan.bullet2": "Revenue model with scenario modeling",
    "upsell.bizplan.bullet3": "Go-to-market playbook",
    "upsell.bizplan.bullet4": "Product specification",
    "upsell.bizplan.bullet5": "Risk resolution roadmap with milestones",
    "upsell.bizplan.bullet6": "90-day execution plan",
    "upsell.mvp.name": "MVP Build",
    "upsell.mvp.price": "$1,999",
    "upsell.mvp.tagline": "We'll build it for you",
    "upsell.mvp.desc":
      "Technical architecture, implementation plan, and working deployed MVP with mechanical verification and git audit trail. Concierge delivery.",
    "upsell.mvp.includes": "Everything in Business Plan, plus:",
    "upsell.mvp.bullet1": "Technical architecture",
    "upsell.mvp.bullet2": "Implementation plan",
    "upsell.mvp.bullet3": "Working deployed MVP",
    "upsell.mvp.bullet4": "Post-deployment handoff",
    "upsell.bizplan.cta": "Unlock Business Plan -- $249",
    "upsell.mvp.cta": "Contact Us",
    "upsell.bizplan.error.assessment_required":
      "Please complete your assessment first before purchasing the Business Plan.",
    "upsell.coming_soon": "Coming Soon",
    "upsell.footer":
      "Your free assessment is the foundation -- each tier builds on everything we discussed.",

    // Pipeline phases
    "pipeline.generating.business_case": "Generating your business case...",
    "pipeline.generating.product_spec": "Generating your product specification...",
    "pipeline.generating.architecture": "Generating your technical architecture...",
    "pipeline.generating.epic_plan": "Generating your execution plan...",
    "pipeline.review.placeholder": "Pipeline review -- coming soon",
    "pipeline.complete": "Your full business plan is ready!",

    // Pipeline
    "pipeline.starting": "Starting pipeline...",

    // Language labels
    "language.en": "English",
    "language.pt-PT": "Português (Portugal)",
    "language.pt-BR": "Português (Brasil)",
  },

  "pt-PT": {
    // Chat placeholder
    "chat.placeholder.title": "Conta-me a tua ideia",
    "chat.placeholder.subtitle":
      "Descreve a tua ideia de negócio ou conceito de produto. Vou fazer perguntas para compreender a fundo e depois gerar um relatório de viabilidade.",
    "chat.examples.heading": "Founders já testaram ideias como:",
    "chat.examples.1": "Marketplace de IA que liga contabilistas freelance a pequenas empresas",
    "chat.examples.2": "Ferramenta para pais acompanharem a medicação dos filhos",
    "chat.examples.3": "Extensão de browser que resume termos de serviço antes de aceitar",
    "chat.examples.4": "Estamos a construir uma ferramenta de rollback de deploys para pequenas equipas de engenharia. Quando um deploy causa erros, em vez de reverter para a última versão, a nossa ferramenta analisa logs, identifica a alteração específica que causou o problema e sugere um patch mínimo. O nosso alvo são equipas de 5-20 devs que não têm staff dedicado de SRE. O plano gratuito monitoriza um serviço, o plano pago a $79/mês cobre serviços ilimitados. Temos um protótipo funcional integrado com GitHub Actions mas ainda não temos utilizadores.",
    "chat.examples.5": "Estou a construir uma plataforma que liga proprietários de imóveis a auditores energéticos certificados para avaliações pré-venda. A ideia é que os vendedores obtêm um relatório de eficiência energética que podem usar para justificar preços mais altos, e os auditores obtêm um pipeline de leads em vez de fazer cold-calling. Ficaríamos com 15% da taxa de auditoria. A começar em Portugal e Espanha onde as novas regulamentações energéticas da UE estão a tornar estas auditorias obrigatórias. Falei com três auditores que disseram que aderiam mas ainda não construí nada.",
    "chat.input.placeholder.first": "Descreve a tua ideia de negócio...",
    "chat.input.placeholder.reply": "Escreve a tua resposta...",
    "chat.input.placeholder.feedback": "Partilha os teus pensamentos...",
    "chat.input.placeholder.done": "Feedback concluído",
    "chat.send": "Enviar",
    "chat.thinking": "A pensar...",
    "chat.restoring": "A restaurar conversa...",
    "chat.export.title.feedback": "Entrevista de Feedback",
    "chat.export.title.main": "Interrogação de Negócio",

    // Phase 0 progress
    "phase0.step1": "A pesquisar o teu mercado...",
    "phase0.step2": "A analisar concorrentes e rondas de financiamento...",
    "phase0.step3":
      "A recolher dados de mercado e benchmarks de preços...",
    "phase0.step4": "A sintetizar pesquisa para a primeira pergunta...",
    "phase0.step5":
      "Quase pronto -- a preparar uma abertura detalhada com base na pesquisa de mercado...",
    "phase0.note":
      "Isto demora mais do que as respostas normais -- estamos a fazer pesquisa de mercado aprofundada para te dar dados específicos de concorrentes e contexto de mercado na primeira pergunta.",

    // Conversation progress (intermediate rounds)
    "conversation.step1": "A analisar a tua resposta...",
    "conversation.step2": "A preparar a proxima pergunta...",
    "conversation.step3": "A avaliar as tuas respostas ate agora...",
    "conversation.note":
      "Cada pergunta baseia-se nas tuas respostas anteriores para aprofundar a tua ideia de negocio.",

    // Assessment progress (reflects 3-call enhanced synthesis)
    "assessment.step1": "A gerar o teu relatório de viabilidade...",
    "assessment.step2": "A construir sumário executivo e análise de investigação...",
    "assessment.step3": "A analisar dimensões 1 a 5...",
    "assessment.step4": "A analisar dimensões 6 a 10...",
    "assessment.step5": "A compilar inteligência competitiva e planos de ação de risco...",
    "assessment.step6": "A escrever o teu relatório detalhado...",
    "assessment.step7":
      "A finalizar -- este é um relatório abrangente, quase pronto...",
    "assessment.note":
      "Isto demora mais do que as respostas normais -- estamos a preparar o teu relatório completo de 10 dimensões com inteligência competitiva, planos de ação de risco e recomendações.",

    // Assessment / brief
    "assessment.download": "Descarregar Relatório",
    "assessment.download.partial": "Descarregar Relatório Parcial",
    "assessment.download.html": "Ver Relatório Completo",
    "assessment.download.exec": "Ver Resumo Executivo",
    "assessment.download.md": "Descarregar Markdown",
    "assessment.incomplete.warning":
      "Este relatório pode estar incompleto devido a um problema técnico. As conclusões principais estão incluídas acima.",
    "assessment.complete.pilot": "Relatório concluído!",
    "assessment.complete.default": "Brief de negócio concluído!",
    "assessment.ready.pilot":
      "O teu relatório de viabilidade está pronto. Descarrega abaixo.",
    "assessment.ready.default":
      "O teu brief está pronto. Gera um business case para rever antes de construir o plano completo.",
    "assessment.generate_bc": "Gerar Business Case",

    // Feedback
    "feedback.banner": "Entrevista de Feedback",
    "feedback.prompt.title": "Como foi a tua experiência?",
    "feedback.prompt.body":
      "Ajuda-nos a melhorar partilhando uma entrevista rápida de feedback (2-3 minutos).",
    "feedback.cta": "Dar Feedback",
    "feedback.later": "Talvez depois",
    "feedback.complete": "Obrigado pelo teu feedback!",
    "feedback.back": "Voltar ao Dashboard",

    // Quick feedback
    "feedback.quick.title": "Feedback rápido",
    "feedback.quick.placeholder": "Pensamentos rápidos, reações, qualquer coisa...",
    "feedback.quick.send": "Enviar",
    "feedback.quick.sending": "A enviar...",
    "feedback.quick.thanks": "Obrigado pelo teu feedback!",
    "feedback.quick.button": "Feedback",

    // Return nudge
    "nudge.message":
      "O teu relatório está pronto! Gostaríamos de ouvir a tua opinião.",
    "nudge.cta": "Dar Feedback",

    // Upsell
    "upsell.title": "O que o Architect pode fazer a seguir com o teu negócio:",
    "upsell.assessment.name": "Relatório Completo",
    "upsell.assessment.price": "$49",
    "upsell.assessment.tagline": "Desbloqueia o teu relatório completo",
    "upsell.assessment.desc":
      "Relatório completo de 10 dimensões com modelos matemáticos estruturados, dossier de inteligência competitiva, registo de riscos com recomendações direcionais, análise de padrões de decisão do fundador e PDF de resumo executivo partilhável.",
    "upsell.assessment.cta": "Desbloquear Relatório Completo -- $49",
    "upsell.bizplan.name": "Plano de Negócio",
    "upsell.bizplan.price": "$249",
    "upsell.bizplan.tagline": "Obtém uma estratégia de negócio completa",
    "upsell.bizplan.desc":
      "Business case completo com dimensionamento de mercado, posicionamento competitivo, modelo de receita, playbook go-to-market, especificação de produto, roadmap de resolução de riscos e plano de execução de 90 dias. Construído sobre os resultados do teu relatório.",
    "upsell.bizplan.includes": "Tudo no Relatório, mais:",
    "upsell.bizplan.bullet1": "Dimensionamento de mercado e estratégia de posicionamento",
    "upsell.bizplan.bullet2": "Modelo de receita com cenários",
    "upsell.bizplan.bullet3": "Playbook go-to-market",
    "upsell.bizplan.bullet4": "Especificação de produto",
    "upsell.bizplan.bullet5": "Roadmap de resolução de riscos com milestones",
    "upsell.bizplan.bullet6": "Plano de execução de 90 dias",
    "upsell.mvp.name": "MVP Build",
    "upsell.mvp.price": "$1,999",
    "upsell.mvp.tagline": "Nós construímos por ti",
    "upsell.mvp.desc":
      "Arquitetura técnica, plano de implementação e MVP funcional deployed com verificação mecânica e audit trail git. Entrega concierge.",
    "upsell.mvp.includes": "Tudo no Plano de Negócio, mais:",
    "upsell.mvp.bullet1": "Arquitetura técnica",
    "upsell.mvp.bullet2": "Plano de implementação",
    "upsell.mvp.bullet3": "MVP funcional deployed",
    "upsell.mvp.bullet4": "Handoff pós-deployment",
    "upsell.bizplan.cta": "Desbloquear Plano de Negócio -- $249",
    "upsell.mvp.cta": "Contactar-nos",
    "upsell.bizplan.error.assessment_required":
      "Por favor, conclui o teu relatório antes de comprar o Plano de Negócio.",
    "upsell.coming_soon": "Em Breve",
    "upsell.footer":
      "O teu relatório gratuito é a base -- cada plano constrói sobre tudo o que discutimos.",

    // Pipeline phases
    "pipeline.generating.business_case": "A gerar o teu business case...",
    "pipeline.generating.product_spec": "A gerar a tua especificação de produto...",
    "pipeline.generating.architecture": "A gerar a tua arquitetura técnica...",
    "pipeline.generating.epic_plan": "A gerar o teu plano de execução...",
    "pipeline.review.placeholder": "Revisão do pipeline -- em breve",
    "pipeline.complete": "O teu plano de negócio completo está pronto!",

    // Pipeline
    "pipeline.starting": "A iniciar pipeline...",

    // Language labels
    "language.en": "English",
    "language.pt-PT": "Português (Portugal)",
    "language.pt-BR": "Português (Brasil)",
  },

  "pt-BR": {
    // Chat placeholder
    "chat.placeholder.title": "Me conta sua ideia",
    "chat.placeholder.subtitle":
      "Descreva sua ideia de negócio ou conceito de produto. Vou fazer perguntas para entender a fundo e depois gerar um relatório de viabilidade.",
    "chat.examples.heading": "Founders já testaram ideias como:",
    "chat.examples.1": "Marketplace de IA conectando contadores freelance a pequenas empresas",
    "chat.examples.2": "Ferramenta para pais acompanharem a medicação dos filhos",
    "chat.examples.3": "Extensão de navegador que resume termos de serviço antes de aceitar",
    "chat.examples.4": "Estamos construindo uma ferramenta de rollback de deploys para pequenas equipes de engenharia. Quando um deploy causa erros, em vez de reverter para a última versão, nossa ferramenta analisa logs, identifica a mudança específica que causou o problema e sugere um patch mínimo. Nosso alvo são equipes de 5-20 devs que não têm staff dedicado de SRE. O plano gratuito monitora um serviço, o plano pago a $79/mês cobre serviços ilimitados. Temos um protótipo funcional integrado com GitHub Actions mas ainda não temos usuários.",
    "chat.examples.5": "Estou construindo uma plataforma que conecta proprietários de imóveis a auditores energéticos certificados para avaliações pré-venda. A ideia é que os vendedores obtêm um relatório de eficiência energética que podem usar para justificar preços mais altos, e os auditores obtêm um pipeline de leads em vez de fazer cold-calling. Ficaríamos com 15% da taxa de auditoria. Começando em Portugal e Espanha onde as novas regulamentações energéticas da UE estão tornando essas auditorias obrigatórias. Conversei com três auditores que disseram que participariam mas ainda não construí nada.",
    "chat.input.placeholder.first": "Descreva sua ideia de negócio...",
    "chat.input.placeholder.reply": "Digite sua resposta...",
    "chat.input.placeholder.feedback": "Compartilhe seus pensamentos...",
    "chat.input.placeholder.done": "Feedback concluído",
    "chat.send": "Enviar",
    "chat.thinking": "Pensando...",
    "chat.restoring": "Restaurando conversa...",
    "chat.export.title.feedback": "Entrevista de Feedback",
    "chat.export.title.main": "Interrogação de Negócio",

    // Phase 0 progress
    "phase0.step1": "Pesquisando seu mercado...",
    "phase0.step2": "Analisando concorrentes e rodadas de financiamento...",
    "phase0.step3":
      "Coletando dados de mercado e benchmarks de preços...",
    "phase0.step4": "Sintetizando pesquisa para a primeira pergunta...",
    "phase0.step5":
      "Quase pronto -- preparando uma abertura detalhada com base na pesquisa de mercado...",
    "phase0.note":
      "Isso demora mais do que as respostas normais -- estamos fazendo pesquisa de mercado aprofundada para dar dados específicos de concorrentes e contexto de mercado na primeira pergunta.",

    // Conversation progress (intermediate rounds)
    "conversation.step1": "Analisando sua resposta...",
    "conversation.step2": "Preparando a proxima pergunta...",
    "conversation.step3": "Avaliando suas respostas ate agora...",
    "conversation.note":
      "Cada pergunta se baseia nas suas respostas anteriores para aprofundar sua ideia de negocio.",

    // Assessment progress (reflects 3-call enhanced synthesis)
    "assessment.step1": "Gerando seu relatório de viabilidade...",
    "assessment.step2": "Construindo sumário executivo e análise de investigação...",
    "assessment.step3": "Analisando dimensões 1 a 5...",
    "assessment.step4": "Analisando dimensões 6 a 10...",
    "assessment.step5": "Compilando inteligência competitiva e planos de ação de risco...",
    "assessment.step6": "Escrevendo seu relatório detalhado...",
    "assessment.step7":
      "Finalizando -- este é um relatório abrangente, quase pronto...",
    "assessment.note":
      "Isso demora mais do que as respostas normais -- estamos preparando seu relatório completo de 10 dimensões com inteligência competitiva, planos de ação de risco e recomendações.",

    // Assessment / brief
    "assessment.download": "Baixar Relatório",
    "assessment.download.partial": "Baixar Relatório Parcial",
    "assessment.download.html": "Ver Relatório Completo",
    "assessment.download.exec": "Ver Resumo Executivo",
    "assessment.download.md": "Baixar Markdown",
    "assessment.incomplete.warning":
      "Este relatório pode estar incompleto devido a um problema técnico. As conclusões principais estão incluídas acima.",
    "assessment.complete.pilot": "Relatório concluído!",
    "assessment.complete.default": "Brief de negócio concluído!",
    "assessment.ready.pilot":
      "Seu relatório de viabilidade está pronto. Baixe abaixo.",
    "assessment.ready.default":
      "Seu brief está pronto. Gere um business case para revisar antes de construir o plano completo.",
    "assessment.generate_bc": "Gerar Business Case",

    // Feedback
    "feedback.banner": "Entrevista de Feedback",
    "feedback.prompt.title": "Como foi sua experiência?",
    "feedback.prompt.body":
      "Nos ajude a melhorar compartilhando uma entrevista rápida de feedback (2-3 minutos).",
    "feedback.cta": "Dar Feedback",
    "feedback.later": "Talvez depois",
    "feedback.complete": "Obrigado pelo seu feedback!",
    "feedback.back": "Voltar ao Dashboard",

    // Quick feedback
    "feedback.quick.title": "Feedback rápido",
    "feedback.quick.placeholder": "Pensamentos rápidos, reações, qualquer coisa...",
    "feedback.quick.send": "Enviar",
    "feedback.quick.sending": "Enviando...",
    "feedback.quick.thanks": "Obrigado pelo seu feedback!",
    "feedback.quick.button": "Feedback",

    // Return nudge
    "nudge.message":
      "Seu relatório está pronto! Gostaríamos de ouvir sua opinião.",
    "nudge.cta": "Dar Feedback",

    // Upsell
    "upsell.title": "O que o Architect pode fazer em seguida com seu negócio:",
    "upsell.assessment.name": "Relatório Completo",
    "upsell.assessment.price": "$49",
    "upsell.assessment.tagline": "Desbloqueie seu relatório completo",
    "upsell.assessment.desc":
      "Relatório completo de 10 dimensões com modelos matemáticos estruturados, dossiê de inteligência competitiva, registro de riscos com recomendações direcionais, análise de padrões de decisão do fundador e PDF de resumo executivo compartilhável.",
    "upsell.assessment.cta": "Desbloquear Relatório Completo -- $49",
    "upsell.bizplan.name": "Plano de Negócio",
    "upsell.bizplan.price": "$249",
    "upsell.bizplan.tagline": "Tenha uma estratégia de negócio completa",
    "upsell.bizplan.desc":
      "Business case completo com dimensionamento de mercado, posicionamento competitivo, modelo de receita, playbook go-to-market, especificação de produto, roadmap de resolução de riscos e plano de execução de 90 dias. Construído sobre os resultados do seu relatório.",
    "upsell.bizplan.includes": "Tudo no Relatório, mais:",
    "upsell.bizplan.bullet1": "Dimensionamento de mercado e estratégia de posicionamento",
    "upsell.bizplan.bullet2": "Modelo de receita com cenários",
    "upsell.bizplan.bullet3": "Playbook go-to-market",
    "upsell.bizplan.bullet4": "Especificação de produto",
    "upsell.bizplan.bullet5": "Roadmap de resolução de riscos com milestones",
    "upsell.bizplan.bullet6": "Plano de execução de 90 dias",
    "upsell.mvp.name": "MVP Build",
    "upsell.mvp.price": "$1,999",
    "upsell.mvp.tagline": "Nós construímos para você",
    "upsell.mvp.desc":
      "Arquitetura técnica, plano de implementação e MVP funcional implantado com verificação mecânica e audit trail git. Entrega concierge.",
    "upsell.mvp.includes": "Tudo no Plano de Negócio, mais:",
    "upsell.mvp.bullet1": "Arquitetura técnica",
    "upsell.mvp.bullet2": "Plano de implementação",
    "upsell.mvp.bullet3": "MVP funcional implantado",
    "upsell.mvp.bullet4": "Handoff pós-implantação",
    "upsell.bizplan.cta": "Desbloquear Plano de Negócio -- $249",
    "upsell.mvp.cta": "Fale Conosco",
    "upsell.bizplan.error.assessment_required":
      "Por favor, conclua seu relatório antes de comprar o Plano de Negócio.",
    "upsell.coming_soon": "Em Breve",
    "upsell.footer":
      "Seu relatório gratuito é a base -- cada plano constrói sobre tudo que discutimos.",

    // Pipeline phases
    "pipeline.generating.business_case": "Gerando seu business case...",
    "pipeline.generating.product_spec": "Gerando sua especificação de produto...",
    "pipeline.generating.architecture": "Gerando sua arquitetura técnica...",
    "pipeline.generating.epic_plan": "Gerando seu plano de execução...",
    "pipeline.review.placeholder": "Revisão do pipeline -- em breve",
    "pipeline.complete": "Seu plano de negócio completo está pronto!",

    // Pipeline
    "pipeline.starting": "Iniciando pipeline...",

    // Language labels
    "language.en": "English",
    "language.pt-PT": "Português (Portugal)",
    "language.pt-BR": "Português (Brasil)",
  },
};

export function t(key: string, language: Language): string {
  return translations[language]?.[key] ?? translations["en"][key] ?? key;
}
